import { UseFormReturnType } from '@mantine/form';
import { DeviceGetResponse } from 'api/actions/device-get/device-get-response';
import { DeviceFormData } from 'components/forms/device/DeviceForm';
import EvidenceValueInput, { EvidenceValueDefinition } from 'components/forms/evidence-field/EvidenceValueInput';
import { useEffect } from 'react';

export type DeviceEvidenceFieldType = DeviceGetResponse['evidenceFields'][number] & EvidenceValueDefinition;

type EvidenceFieldProps = DeviceEvidenceFieldType & {
  form: UseFormReturnType<DeviceFormData>;
};

const VALUE_FIELDS: Record<string, keyof DeviceEvidenceFieldType> = {
  text: 'fieldValue',
  number: 'fieldNumberValue',
  date: 'fieldDateValue',
  select: 'fieldSelectValue',
  predefined: 'fieldPredefinedValue',
  checkbox: 'fieldBooleanValue',
};

/** Displays a single evidence field with the controlled input selected by its type. */
export default function DeviceEvidenceField(props: EvidenceFieldProps) {
  const { evidenceFieldId, fieldType, form } = props;
  const valueField = VALUE_FIELDS[fieldType ?? ''];
  const formPath = `evidenceFields.${evidenceFieldId!}.${String(valueField)}`;
  const storedField = evidenceFieldId ? form.values.evidenceFields[evidenceFieldId] : undefined;
  const value = valueField ? storedField?.[valueField] : undefined;

  useEffect(() => {
    if (!evidenceFieldId) return;
    const existing = form.values.evidenceFields[evidenceFieldId];
    if (existing) {
      if (fieldType === 'checkbox' && existing.fieldBooleanValue === undefined) {
        form.setFieldValue(`evidenceFields.${evidenceFieldId}.fieldBooleanValue`, false);
      }
      return;
    }
    form.setFieldValue(`evidenceFields.${evidenceFieldId}`, {
      evidenceFieldId,
      fieldType,
      fieldValue: '',
      fieldNumberValue: undefined,
      fieldDateValue: undefined,
      fieldSelectValue: undefined,
      fieldPredefinedValue: undefined,
      fieldBooleanValue: fieldType === 'checkbox' ? false : undefined,
    });
  }, [fieldType, evidenceFieldId, form]);

  return (
    <EvidenceValueInput
      definition={props}
      value={value as string | number | boolean | null | undefined}
      error={form.errors?.[formPath]}
      onChange={(next) => form.setFieldValue(formPath, next)}
    />
  );
}
